"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  List,
  Tabs,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
  IconPhoto,
  IconDroplet,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";
import OutfitGrid from "@/components/OutfitGrid";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

export default function DeepWinterVsBrightWinterContent() {
  const t = useTranslations("deepWinterVsBrightWinter");

  // Get the subseason data
  const deepWinterData = colorSeasons.WINTER.subseasons.DEEP;
  const brightWinterData = colorSeasons.WINTER.subseasons.TRUE; // TRUE is Bright Winter in the constants

  // State for outfit data
  const [deepWinterOutfits, setDeepWinterOutfits] = useState<OutfitIdea[]>([]);
  const [brightWinterOutfits, setBrightWinterOutfits] = useState<OutfitIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const [deepWinterDoc, brightWinterDoc] = await Promise.all([
          getDoc(doc(db, "outfitIdeas", "WINTER_DEEP")),
          getDoc(doc(db, "outfitIdeas", "WINTER_TRUE")),
        ]);

        setDeepWinterOutfits(deepWinterDoc.data()?.ideas || []);
        setBrightWinterOutfits(brightWinterDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutfitData();
  }, []);

  const comparisonPoints = [
    {
      aspect: "Color Depth",
      deepWinter: "Rich, deep colors with strong saturation and intensity",
      brightWinter: "Clear, bright colors with high contrast and clarity",
      icon: IconPalette,
    },
    {
      aspect: "Undertone",
      deepWinter: "Cool undertones with deep, rich characteristics",
      brightWinter: "Cool undertones with bright, clear characteristics",
      icon: IconEye,
    },
    {
      aspect: "Best For",
      deepWinter: "Deep, rich coloring with high contrast",
      brightWinter: "Clear, bright coloring with striking contrast",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      deepWinter: "Light, pastel colors",
      brightWinter: "Muted, soft colors",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Deep Winter vs Bright Winter
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Discover the key differences between these two cool winter subseasons and find which one perfectly
            complements your natural coloring.
          </Text>

          {/* Season Moodboard */}
          <Box mb="xl">
            <Box
              mih={{ base: "300px", md: "400px" }}
              style={{
                position: "relative",
                borderRadius: "1rem",
                overflow: "hidden",
              }}
            >
              <Image
                src="/WINTER.webp"
                alt="Winter season moodboard"
                width={800}
                height={400}
                style={{
                  borderRadius: "1rem",
                  objectFit: "cover",
                  objectPosition: "center",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison Overview
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{deepWinterData.emoji}</Text>
                <Title order={3}>Deep Winter</Title>
                <Text size="sm" c="dimmed">
                  {deepWinterData.description.substring(0, 150)}...
                </Text>
                <Badge color="blue" variant="light">
                  Rich & Deep
                </Badge>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{brightWinterData.emoji}</Text>
                <Title order={3}>Bright Winter</Title>
                <Text size="sm" c="dimmed">
                  {brightWinterData.description.substring(0, 150)}...
                </Text>
                <Badge color="blue" variant="light">
                  Clear & Bright
                </Badge>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="blue" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Deep Winter:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.deepWinter}
                    </Text>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Bright Winter:
                    </Text>
                    <Text size="sm">{point.brightWinter}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Detailed Color Palette Comparison */}
        <Tabs defaultValue="deep-winter" variant="outline">
          <Tabs.List justify="center" mb="xl">
            <Tabs.Tab value="deep-winter" leftSection={<IconDroplet size={16} />}>
              Deep Winter Palette
            </Tabs.Tab>
            <Tabs.Tab value="bright-winter" leftSection={<IconDroplet size={16} />}>
              Bright Winter Palette
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="deep-winter">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                Deep Winter Color Palette
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                Rich, deep colors with strong saturation and intensity for cool undertones
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Primary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {deepWinterData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Neutral Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {deepWinterData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Complementary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {deepWinterData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="bright-winter">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                Bright Winter Color Palette
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                Cool, clear colors with high saturation that enhance cool undertones
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Primary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {brightWinterData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Neutral Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {brightWinterData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Complementary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {brightWinterData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Characteristics Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Characteristics & Styling
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} mb="md" ta="center" c="blue">
                Deep Winter
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {deepWinterData.characteristics.map((char, index) => (
                      <Badge key={index} color="blue" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {deepWinterData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center" c="blue">
                Bright Winter
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {brightWinterData.characteristics.map((char, index) => (
                      <Badge key={index} color="blue" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {brightWinterData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Outfit Samples */}
        <Divider my="xl" />
        <Box>
          <Title order={2} mb="lg" ta="center">
            Outfit Inspiration
          </Title>
          <Text size="sm" c="dimmed" ta="center" mb="xl">
            See how these color palettes translate into real outfit combinations
          </Text>

          <Tabs defaultValue="deep-winter-outfits" variant="outline">
            <Tabs.List justify="center" mb="xl">
              <Tabs.Tab value="deep-winter-outfits" leftSection={<IconPhoto size={16} />}>
                Deep Winter Outfits
              </Tabs.Tab>
              <Tabs.Tab value="bright-winter-outfits" leftSection={<IconPhoto size={16} />}>
                Bright Winter Outfits
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="deep-winter-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>Loading outfit samples...</Text>
                </Box>
              ) : deepWinterOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={deepWinterOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">No outfit samples available for Deep Winter</Text>
                </Box>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="bright-winter-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>Loading outfit samples...</Text>
                </Box>
              ) : brightWinterOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={brightWinterOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">No outfit samples available for Bright Winter</Text>
                </Box>
              )}
            </Tabs.Panel>
          </Tabs>
        </Box>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Black Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Wear pure black near your face and notice the effect:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Deep Winter:</strong> Black feels natural and grounding</List.Item>
                <List.Item><strong>Bright Winter:</strong> Black is strong but icy white may be even better</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Saturation Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare a deep burgundy vs a bright fuchsia:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Deep Winter:</strong> The deep burgundy enhances your features</List.Item>
                <List.Item><strong>Bright Winter:</strong> The bright fuchsia makes you glow</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between Deep Winter and Bright Winter.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/winter/deep"
                size="sm"
                color="gray"
              >
                View Deep Winter Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/winter/true"
                size="sm"
                color="gray"
              >
                View Bright Winter Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
